'use client';

import { CardType } from '@/config/dashboardWorkflows';
import { useState } from 'react';

// Label colors mapping (fallback for color_display; mirrors LABEL_COLORS in models.py)
const LABEL_COLORS: { [key: number]: string } = {
  0: '#34b27d',
  1: '#dbdb57',
  2: '#e09952',
  3: '#cb4d4d',
  4: '#9933cc',
  5: '#4d77cb',
};

interface TaskCardProps {
  title: string;
  taskId: string;
  taskIdNum?: number;
  onClick?: () => void;
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
  isSprintView?: boolean;
  isDashboardView?: boolean; // NEW: Dashboard view mode
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, taskId: number | string) => void; // Updated to support both number and string
  status?: number; // 0 = Not Started, 1 = In Progress, 2 = Testing, 3 = Done
  type?: number; // 0 = Feature, 1 = Bug
  estimatedHours?: number | null;
  childCount?: number;
  labels?: Array<{
    id: number;
    name: string;
    color: number;
    color_display: string;
  }>;
  release?: {
    id: number;
    name: string;
  } | null;
  // Dashboard-specific props
  cardType?: CardType | null;
  stage?: number; // Dashboard workflow stage
  dashboardData?: {
    publish_date?: string;
    author?: string;
    word_count?: number;
    tags?: string[];
    url?: string;
    [key: string]: unknown; // Allow additional fields from data JSONField
  };
  // Kanban board (sprint view) specific
  onRemoveFromSprint?: (taskId: number) => void;
  // Content editor for whitepapers
  onEditContent?: (taskId: number) => void;
}

export default function TaskCard({ 
  title, 
  taskId, 
  taskIdNum, 
  onClick, 
  assignedTo, 
  isSprintView = false,
  isDashboardView = false,
  draggable = false, 
  onDragStart, 
  status, 
  type, 
  estimatedHours, 
  childCount, 
  labels, 
  release,
  cardType,
  stage,
  dashboardData,
  onRemoveFromSprint,
  onEditContent
}: TaskCardProps) {
  // Dashboard view rendering
  if (isDashboardView) {
    const borderColors = {
      blue: 'border-l-blue-500',
      purple: 'border-l-purple-500',
      amber: 'border-l-amber-500',
      pink: 'border-l-pink-500',
      emerald: 'border-l-emerald-500',
      cyan: 'border-l-cyan-500',
    };

    const typeConfig: { [key: string]: { label: string; icon: string; badge: string; color: keyof typeof borderColors } } = {
      whitepaper: { label: 'Whitepaper', icon: '◈', badge: 'bg-blue-500/20 text-blue-300', color: 'blue' },
      blog: { label: 'Blog Post', icon: '¶', badge: 'bg-purple-500/20 text-purple-300', color: 'purple' },
      podcast: { label: 'Podcast', icon: '●', badge: 'bg-amber-500/20 text-amber-300', color: 'amber' },
      landing_page: { label: 'Landing Page', icon: '◇', badge: 'bg-rose-500/20 text-rose-300', color: 'pink' },
      campaign: { label: 'Campaign', icon: '◆', badge: 'bg-pink-500/20 text-pink-300', color: 'pink' },
      client: { label: 'Client', icon: '👤', badge: 'bg-emerald-500/20 text-emerald-300', color: 'emerald' },
      opportunity: { label: 'Opportunity', icon: '💼', badge: 'bg-cyan-500/20 text-cyan-300', color: 'cyan' },
      grant: { label: 'Grant', icon: '💰', badge: 'bg-blue-500/20 text-blue-300', color: 'blue' },
      accelerator: { label: 'Accelerator', icon: '🚀', badge: 'bg-purple-500/20 text-purple-300', color: 'purple' },
      vc_contact: { label: 'VC Contact', icon: '🤝', badge: 'bg-amber-500/20 text-amber-300', color: 'amber' },
      product_feature: { label: 'Product Feature', icon: '⚡', badge: 'bg-blue-500/20 text-blue-300', color: 'blue' },
      data_import: { label: 'Data Import', icon: '📊', badge: 'bg-purple-500/20 text-purple-300', color: 'purple' },
      manufacturer: { label: 'Manufacturer', icon: '🏭', badge: 'bg-amber-500/20 text-amber-300', color: 'amber' },
      distributor: { label: 'Distributor', icon: '🚚', badge: 'bg-emerald-500/20 text-emerald-300', color: 'emerald' },
      product_line: { label: 'Product Line', icon: '📦', badge: 'bg-pink-500/20 text-pink-300', color: 'pink' },
    };

    const config = cardType ? (typeConfig[cardType] || typeConfig.blog) : typeConfig.blog;
    const borderColor = borderColors[config.color] || borderColors.purple;

    const handleDragStart = (e: React.DragEvent) => {
      if (draggable && onDragStart) {
        onDragStart(e, taskId); // Use taskId string for dashboard tasks
      }
    };

    const handleEditContent = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      if (taskIdNum && onEditContent) {
        onEditContent(taskIdNum);
      }
    };

    // Check if Google Docs link exists for whitepaper or blog
    const hasGoogleDocsLink = (cardType === 'whitepaper' || cardType === 'blog') &&
      typeof dashboardData?.google_docs_link === 'string' &&
      dashboardData.google_docs_link.trim().length > 0;

    return (
      <div 
        onClick={onClick}
        draggable={draggable}
        onDragStart={handleDragStart}
        className={`bg-gray-100 rounded-lg p-2.5 border-l-4 ${borderColor} shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
          draggable ? 'cursor-move' : ''
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${config.badge}`}>
            {config.icon} {config.label}
          </span>
          {hasGoogleDocsLink && taskIdNum && onEditContent && (
            <button
              onClick={handleEditContent}
              className="text-[10px] px-2 py-0.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-600 rounded font-medium transition-colors flex items-center gap-1"
              title="Open Google Docs"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Edit Content
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-800 mb-1.5">{title}</div>
        <div className="flex items-center justify-between text-[11px] text-gray-500">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            <span>Assign to:</span>
            {assignedTo && (assignedTo.full_name || assignedTo.email) ? (
              <span className="text-gray-700 font-medium">{assignedTo.full_name || assignedTo.email}</span>
            ) : (
              <span className="text-gray-700 font-medium">me</span>
            )}
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <span>{taskId}</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // Kanban board (sprint view) rendering (existing)
  if (isSprintView) {
    const [isHovered, setIsHovered] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const handleDragStart = (e: React.DragEvent) => {
      if (taskIdNum && onDragStart) {
        onDragStart(e, taskIdNum);
      }
    };

    const handleRemoveFromSprintClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (taskIdNum && onRemoveFromSprint) {
        onRemoveFromSprint(taskIdNum);
        setShowDropdown(false);
      }
    };

    // Get gradient and border color based on status
    const getStatusStyle = () => {
      switch (status) {
        case 0: // Not Started - Green gradient
          return {
            background: 'linear-gradient(to bottom, #8fde8f, #69b469)',
            borderColor: '#69b469',
          };
        case 1: // In Progress - Blue gradient
          return {
            background: 'linear-gradient(to bottom, #a0bfdc, #7ab6e9)',
            borderColor: '#7ab6e9',
          };
        case 2: // Delivered/Testing - Yellow/tan gradient
          return {
            background: 'linear-gradient(to bottom, #dfca9c, #c7b181)',
            borderColor: '#c7b181',
          };
        case 3: // Done - Gray gradient
          return {
            background: 'linear-gradient(to bottom, #cecece, #c1c1c1)',
            borderColor: '#c1c1c1',
          };
        default:
          return {
            background: 'linear-gradient(to bottom, #8fde8f, #69b469)',
            borderColor: '#69b469',
          };
      }
    };

    const statusStyle = getStatusStyle();
    const labelCount = labels?.length || 0;
    const labelHeight = labelCount > 0 ? 100 / labelCount : 0;
    const topMultiple = labelHeight === 100 ? 0 : labelHeight; // If single label, topMultiple is 0

    return (
      <div 
        onClick={onClick}
        draggable={true}
        onDragStart={handleDragStart}
        onDragOver={(e) => {
          // Allow dropping on this card - prevent default to allow drop
          // This is necessary for draggable elements to accept drops
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setShowDropdown(false);
        }}
        data-task-id={taskIdNum}
        style={{
          background: statusStyle.background,
          borderColor: statusStyle.borderColor,
          cursor: 'move',
        }}
        className="relative w-[92%] min-w-[195px] mx-auto mb-[7px] pb-[7px] rounded-[2px] border shadow-[1px_1px_1px_1px_rgba(0,0,0,0.5)] hover:shadow-[2px_2px_2px_2px_rgba(0,0,0,0.5)] transition-all"
      >
        {/* Label Stripes (Right Side) */}
        {labels && labels.length > 0 && (
          <div className="absolute right-0 top-0 bottom-0 w-[5px]">
            {labels.map((label, index) => {
              // Calculate top position: accumulate from previous labels
              const top = index * topMultiple;
              // Get color - use color_display if available, otherwise map from LABEL_COLORS
              const color = label.color_display || LABEL_COLORS[label.color] || '#34b27d';
              
              return (
                <span
                  key={label.id}
                  style={{
                    position: 'absolute',
                    top: `${top}%`,
                    right: '0px',
                    backgroundColor: color,
                    width: '5px',
                    height: `${labelHeight}%`,
                    overflow: 'hidden',
                  }}
                >
                  &nbsp;
                </span>
              );
            })}
          </div>
        )}

        {/* Header Section - 87% width, 20px height */}
        <div className="w-[87%] mx-auto h-5 flex items-center justify-between">
          {/* Assigned to Section - float left, margin-top 3px */}
          <div 
            className="flex items-center cursor-pointer"
            style={{
              float: 'left',
              marginTop: '10px',
              color: '#000',
              fontSize: '7pt',
              fontWeight: 'bold',
              verticalAlign: 'middle',
            }}
          >
            {/* User icon - opacity 0.45, margin-top -1px */}
            <svg 
              className="inline-block flex-shrink-0" 
              style={{
                opacity: 0.45,
                marginTop: '-1px',
                display: 'inline-block',
                width: '12px',
                height: '12px',
              }}
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
            {/* "Assigned to:" text - opacity 0.45, margin-left 3px */}
            <span 
              style={{
                opacity: 0.45,
                marginLeft: '3px',
              }}
            >
              Assigned to:
            </span>
            {/* User name - opacity 0.75 - only show if assigned */}
            {assignedTo && (assignedTo.full_name || assignedTo.email) && (
              <span 
                className="truncate"
                style={{
                  opacity: 0.75,
                }}
              >
                {assignedTo.full_name || assignedTo.email}
              </span>
            )}
            {/* Caret icon - 8px x 9px, orange carat */}
            <svg
              className="inline-block"
              style={{
                width: '8px',
                height: '9px',
                display: 'inline-block',
                marginLeft: '3px',
              }}
              viewBox="0 0 8 9"
              fill="none"
            >
              <path d="M4 9L0 0H8L4 9Z" fill="#FF9500" />
            </svg>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 text-[7pt] font-bold">
            {/* Child task count */}
            {childCount !== undefined && childCount > 0 && (
              <span className="text-[7pt]">{childCount}</span>
            )}
            {/* Time estimate */}
            {estimatedHours !== null && estimatedHours !== undefined && estimatedHours > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[7pt]">{estimatedHours.toFixed(1)}</span>
              </div>
            )}
            {/* Bug icon */}
            {type === 1 && (
              <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* Divider - 87% width, 25% opacity */}
        <hr className="w-[87%] mx-auto my-2 opacity-[0.25] border border-groove border-black" />

        {/* Title Section - 87% width, 10pt bold, #2b333a */}
        <div className="w-[87%] mx-auto text-[10pt] font-bold text-[#2b333a] cursor-pointer">
          {title}
        </div>

        {/* Detail Section - 87% width, relative, min-height 20px */}
        <div className="w-[87%] mx-auto relative min-h-[20px] mt-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Task ID - 7pt, bold, #000 at 0.5 opacity, float left, height 27px */}
              <span 
                className="task-easy-id"
                style={{
                  fontSize: '7pt',
                  color: '#000',
                  opacity: 0.5,
                  fontWeight: 'bold',
                  float: 'left',
                  height: '27px',
                }}
              >
                {taskId}
              </span>
              {release && (
                <span className="text-[7pt] opacity-50">[{release.name}]</span>
              )}
            </div>
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Dropdown button - appears on hover in bottom right */}
        {isHovered && (
          <div className="absolute bottom-1 right-1 z-10">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              className="bg-black/60 hover:bg-black/80 text-white rounded p-1.5 shadow-lg transition-colors cursor-pointer"
              title="More options"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            
            {/* Dropdown menu */}
            {showDropdown && (
              <div className="absolute bottom-full right-0 mb-1 bg-[#1a1f3a] border border-[#2d1b3d] rounded-md shadow-xl min-w-[160px] z-20">
                <button
                  onClick={handleRemoveFromSprintClick}
                  className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#2d1b3d] transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove from sprint
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Project view rendering (default)
  const handleDragStart = (e: React.DragEvent) => {
    if (draggable && onDragStart) {
      if (taskIdNum !== undefined) {
        onDragStart(e, taskIdNum);
      } else {
        onDragStart(e, taskId);
      }
    }
  };

  return (
    <div
      onClick={onClick}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragOver={(e) => {
        // Allow this card to be a drop target so it can be reordered against
        e.preventDefault();
      }}
      data-task-id={taskIdNum}
      className={`relative overflow-hidden bg-gradient-to-b from-slate-100 to-slate-300 border border-slate-400 rounded-lg py-2 mb-2 hover:border-purple-500 transition-colors cursor-pointer ${
        labels && labels.length > 0 ? 'pl-4 pr-3' : 'px-3'
      } ${draggable ? 'cursor-move' : ''}`}
    >
      {/* Label color bar (left edge) */}
      {labels && labels.length > 0 && (
        <div className="absolute left-0 top-0 bottom-0 w-[5px]">
          {labels.map((label, index) => {
            const segH = 100 / labels.length;
            const color = label.color_display || LABEL_COLORS[label.color] || '#34b27d';
            return (
              <span
                key={label.id}
                title={label.name ?? ''}
                style={{
                  position: 'absolute',
                  top: `${index * segH}%`,
                  left: 0,
                  width: '5px',
                  height: `${segH}%`,
                  backgroundColor: color,
                }}
              />
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-gray-900 font-semibold text-xs leading-snug">{title}</h4>
      </div>
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-600 font-medium">{taskId}</span>
          <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

